/**
 * GameThumb — small per-game hero thumbnail for lobby tiles.
 *
 * Replaces the old `<div class="tile-icon"><span class="tile-glyph">{name[0]}</span></div>`
 * first-letter rendering. Each game has an `icon.svg` next to its
 * manifest with a small themed illustration (sword crossing for stick-
 * brawl, lit candle for witch-switch, etc.) — the lobby just wasn't
 * using them.
 *
 * Strategy: Vite's `import.meta.glob` pulls every `games/*\/icon.svg`
 * as raw text at build time. We inject the SVG directly via
 * dangerouslySetInnerHTML so the browser renders it as inline SVG
 * (sharper than rendering as an `<img>`, scales without aliasing,
 * inherits CSS color via `currentColor` if the SVG uses it).
 *
 * Per docs/ui/menu-screens.md §4.3 (Home tiles) and
 * docs/ui/room-screens.md §4.6 (Free-mode menu board).
 */

// eager:true so SVG strings are available synchronously on first render
// — no flash of placeholder. as:'raw' returns the file contents as a
// string. The glob pattern walks up two levels from web/src/ to the
// repo's `games/<id>/icon.svg`.
const ICONS = import.meta.glob('../../../../games/*/icon.svg', {
  eager: true,
  query: '?raw',
  import: 'default',
}) as Record<string, string>;

/** Map from gameId → raw SVG string. Built once at module load. */
const SVG_BY_GAME: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const path of Object.keys(ICONS)) {
    // path looks like "../../../../games/stick-brawl/icon.svg" — pull
    // out the segment between "games/" and "/icon.svg".
    const m = /games\/([^/]+)\/icon\.svg$/.exec(path);
    if (!m) continue;
    out[m[1]!] = ICONS[path]!;
  }
  return out;
})();

/** Category → background gradient for tiles that lack a per-game icon. */
const CAT_GRADIENT: Record<string, string> = {
  reflex:   'radial-gradient(circle at 30% 30%, #ff8c42, #c25516)',
  social:   'radial-gradient(circle at 30% 30%, #b876c4, #5a2870)',
  puzzle:   'radial-gradient(circle at 30% 30%, #6dd3c4, #1f6f64)',
  creative: 'radial-gradient(circle at 30% 30%, #e8b14c, #6b4a26)',
  trivia:   'radial-gradient(circle at 30% 30%, #f4e3c2, #6b5b41)',
};

interface Props {
  gameId: string;
  category?: string;
  name?: string;
  /** Pixel size of the tile thumb. Default 96px. */
  size?: number;
  /** When true, show a small chaoticness lightning row at the corner. */
  chaoticness?: number;
}

export function GameThumb({ gameId, category, name, size = 96, chaoticness }: Props) {
  const baseGameId = gameId.split(':')[0] ?? gameId;
  const svg = SVG_BY_GAME[baseGameId];
  const fallbackBg = CAT_GRADIENT[category ?? 'reflex'] ?? CAT_GRADIENT['reflex']!;
  return (
    <div
      className="game-thumb"
      data-cat={category}
      style={{ width: size, height: size, background: svg ? undefined : fallbackBg }}
      aria-hidden="true"
    >
      {svg
        ? <div className="game-thumb-art" dangerouslySetInnerHTML={{ __html: svg }} />
        : <span className="game-thumb-fallback">{name?.[0] ?? '?'}</span>
      }
      {chaoticness && chaoticness > 0 && (
        <span className="game-thumb-chaos" title={`Chaoticness ${chaoticness}/5`}>
          {'⚡'.repeat(Math.min(5, chaoticness))}
        </span>
      )}
    </div>
  );
}
